import React, { useState, useEffect } from "react";
import { getFilterData } from "../../../../Api/Api";
import { CELL_CHEMISTRY_FILTER_CONFIG, parseCellChemistryOptions } from "../utils/cellChemistryFilter";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from "@mui/material";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import { PrimaryTooltip } from "../../../../Components/StyledComponent";
import { FilterIconSvg } from "../../../../Features/Table/Icons";
import LocationFilter from "../../../BmsFaults/Analytics/DetailsFilters/LocationFilter";
import GlobalFilter from "../../../Overview/components/GlobalFilter";
import { useCanUseGlobalAdvancedFilter } from "../../../../Helper/Permissions/GlobalAdvancedFilter";
import AppCache from "../../../../Helper/Common/AppCache";
import SOHCategoryFilter from "./Filters/SOHCategoryFilter";
import StaticOptionsFilter from "./Filters/StaticOptionsFilter";
import NumericRangeFilter from "./Filters/NumericRangeFilter";
import OperationalStatusFilter from "../../../Common/OperationalStatusFilter";

const CONFIG_FILTER_CONFIG = {
  type: "dynamic",
  queryParams: { table: "batteryConfigList", column: "configName" },
};


// Protocol options come from protocolMeta, which is org-scoped but NOT battery-scoped:
// getFilterDataQuery only joins the config base query for the vehicleConfig/configBOP
// tables. So the list can include a protocol no visible battery uses, matching the
// Protocol filter on the Energy Consumption table.
const PROTOCOL_FILTER_CONFIG = {
  type: "dynamic",
  queryParams: { table: "protocolMeta", column: "protocolName" },
};

// Manufacturing year: fetch manufacturingDate from configBOP and extract year
const MFG_DATE_FILTER_CONFIG = {
  type: "dynamic",
  queryParams: { table: "configBOP", column: "manufacturingDate" },
};

// stringLevelState.sohConfidence is a free-text column with these three values.
const STRING_SOH_CONFIDENCE_OPTIONS = ["High", "Medium", "Low"];

const getFilterLabel = (config, defaultLabel) =>
  typeof config === "object" && config?.label ? config.label : defaultLabel;

// Battery-level SOH confidence filter: shown when the org-meta entry is truthy and not
// explicitly { enabled: false }. Options come from its staticData ({ id, value }) and are
// sent verbatim — the backend IN filter on batteryLevelState.sohConfidence is case-sensitive.
const isSohConfidenceFilterEnabled = (config) =>
  Boolean(config) && !(typeof config === "object" && config.enabled === false);

const getSohConfidenceOptions = (config) => {
  const options = (Array.isArray(config?.staticData) ? config.staticData : [])
    .map((o) => o?.value ?? o?.id)
    .filter(Boolean);
  return options.length > 0 ? options : STRING_SOH_CONFIDENCE_OPTIONS;
};

// Range filters show the unit as an input adornment and in the applied-value chip, so an
// org reporting a different unit can override it alongside the label.
const getFilterUnit = (config, defaultUnit) =>
  typeof config === "object" && config?.unit ? config.unit : defaultUnit;

function extractYearsFromDates(filterData) {
  const years = new Set();
  (filterData || []).forEach((item) => {
    if (!item.value) return;
    const year = new Date(item.value).getFullYear();
    if (!isNaN(year)) years.add(String(year));
  });
  return [...years].sort();
}

// ─── Component ────────────────────────────────────────────────────────────────
const SOHDetailsSettingsSidebar = ({
  isExpanded,
  onToggle,
  onFiltersChange,
  allOnboardingLocationsHierarchy = [],
  allFleets = [],
  selectedFleetIds = [],
  filtersConfig = {},
  filters = {},
}) => {
  const canUseGlobalAdvancedFilter = useCanUseGlobalAdvancedFilter();
  const globalFilterConfig = AppCache.getItem("globalFilter")?.find((f) => f.type === "unifiedGlobalFilter") || null;

  const [activeTab,              setActiveTab]              = useState(0);
  const [selectedLocation,       setSelectedLocation]       = useState([]);
  const [resetKey,               setResetKey]               = useState(0);
  const [configOptions,          setConfigOptions]          = useState([]);
  const [mfgYearOptions,         setMfgYearOptions]         = useState([]);
  const [cellChemistryOptions,   setCellChemistryOptions]   = useState([]);
  const [protocolOptions,        setProtocolOptions]        = useState([]);

  useEffect(() => {
    if (filtersConfig.configuration) {
      getFilterData(CONFIG_FILTER_CONFIG).then((res) => {
        const data = res?.response?.response?.filterData || [];
        setConfigOptions(data.map((item) => item.value ?? item.id).filter(Boolean));
      });
    }
  }, [filtersConfig.configuration]);

  useEffect(() => {
    if (filtersConfig.manufacturingYear) {
      getFilterData(MFG_DATE_FILTER_CONFIG).then((res) => {
        const data = res?.response?.response?.filterData || [];
        setMfgYearOptions(extractYearsFromDates(data));
      });
    }
  }, [filtersConfig.manufacturingYear]);

  useEffect(() => {
    if (filtersConfig.cellChemistry) {
      getFilterData(CELL_CHEMISTRY_FILTER_CONFIG).then((res) => {
        // Sorted and de-duplicated, same list and order as the card quick filter.
        setCellChemistryOptions(parseCellChemistryOptions(res?.response?.response?.filterData));
      });
    }
  }, [filtersConfig.cellChemistry]);

  useEffect(() => {
    if (filtersConfig.protocol) {
      getFilterData(PROTOCOL_FILTER_CONFIG).then((res) => {
        const data = res?.response?.response?.filterData || [];
        setProtocolOptions(data.map((item) => item.value ?? item.id).filter(Boolean));
      });
    }
  }, [filtersConfig.protocol]);

  const handleLocationChange = (loc) => {
    setSelectedLocation(loc);
    if (onFiltersChange) onFiltersChange({ location: loc });
  };

  // GlobalFilter applies business segment, customer account and location through one
  // onChange, and the two customer panels only travel as fleet ids.
  const handleGlobalFilterChange = ({ fleetIds, hierarchicalLocation }) => {
    setSelectedLocation(hierarchicalLocation);
    if (onFiltersChange) onFiltersChange({ location: hierarchicalLocation, fleets: fleetIds });
  };

  const handleResetAll = () => {
    setSelectedLocation([]);
    setResetKey((k) => k + 1);
    if (onFiltersChange) {
      onFiltersChange({
        location:             [],
        sohCategories:        [],
        stringSohCategories:  [],
        stringSohConfidence:  [],
        sohConfidence:        [],
        configuration:        [],
        manufacturingYear:    [],
        operationalStatus:    [],
        cellChemistry:        [],
        protocol:             [],
        fleets:               [],
        odometer:             { start: "", end: "" },
      });
    }
  };

  const hasAnyFilter =
    filtersConfig.location ||
    filtersConfig.fleetName ||
    filtersConfig.fleetCategory ||
    filtersConfig.sohCategory ||
    filtersConfig.stringSoh ||
    filtersConfig.stringSohConfidence ||
    isSohConfidenceFilterEnabled(filtersConfig.sohConfidence) ||
    filtersConfig.configuration ||
    filtersConfig.manufacturingYear ||
    filtersConfig.operationalStatus ||
    filtersConfig.cellChemistry ||
    filtersConfig.protocol ||
    filtersConfig.odometer;

  return (
    <>
      {/* ── Collapse / expand toggle ─────────────────────────────────────────── */}
      <IconButton
        onClick={onToggle}
        sx={{
          position: "absolute",
          top: "2px",
          right: isExpanded ? "320px" : "0px",
          zIndex: 1000,
          backgroundColor: "transparent",
          width: "20px",
          height: "20px",
          transition: "right 0.3s ease-in-out",
          boxShadow: "none",
          "&:hover": { backgroundColor: "transparent" },
          "&:focus": { backgroundColor: "transparent" },
        }}
      >
        <PrimaryTooltip
          title={isExpanded ? "Collapse Panel" : "Expand Panel"}
          placement="bottom"
        >
          <KeyboardTabIcon
            sx={{
              fontSize: 20,
              color: "#494949",
              transform: isExpanded ? "rotate(0deg)" : "rotate(180deg)",
              "&:hover": { color: "#6D72F6" },
            }}
          />
        </PrimaryTooltip>
      </IconButton>

      {/* ── Sidebar panel ────────────────────────────────────────────────────── */}
      <Box
        sx={{
          width: isExpanded ? "320px" : "0px",
          transition: "width 0.3s ease-in-out",
          borderLeft: isExpanded ? "1px solid #E6E6E6" : "none",
          backgroundColor: "#FFFFFF",
          height: "100%",
          overflow: "hidden",
          flexShrink: 0,
          my: -2,
          mr: -3,
          position: "fixed",
          right: "24px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            width: "320px",
            opacity: isExpanded ? 1 : 0,
            transition: "opacity 0.3s ease-in-out",
          }}
        >
          {/* ── "Settings" tab header ─────────────────────────────────────── */}
          <Box sx={{ px: 3, flexShrink: 0 }}>
            <Box sx={{ mx: -3 }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{
                  minHeight: "40px",
                  "& .MuiTab-root": {
                    minHeight: "40px",
                    textTransform: "none",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#8D8D8D",
                    p: 2,
                    "&.Mui-selected": { color: "#494949" },
                  },
                  "& .MuiTabs-indicator": { backgroundColor: "#494949" },
                }}
              >
                <Tab label="Settings" />
              </Tabs>
            </Box>
            <Divider sx={{ borderColor: "#E6E6E6", mt: 0, mx: -3 }} />
          </Box>

          {/* ── Scrollable filter content ────────────────────────────────── */}
          <Box
            sx={{
              flex: 1,
              overflowY: "scroll",
              overscrollBehavior: "contain",
              pb: 3,
              "&::-webkit-scrollbar": { width: "6px" },
            }}
          >
            {hasAnyFilter && (
              <Accordion
                defaultExpanded
                sx={{
                  boxShadow: "none",
                  "&:before": { display: "none" },
                  "&.Mui-expanded": { mt: 0, pb: 3 },
                }}
              >
                <AccordionSummary
                  expandIcon={<KeyboardDoubleArrowDownIcon />}
                  sx={{
                    "&.Mui-expanded": { minHeight: "auto" },
                    "& .MuiAccordionSummary-content": { margin: "20px 0" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      pr: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          borderRadius: "4px",
                          backgroundColor: "#EFF0FF",
                          p: 0.75,
                        }}
                      >
                        <FilterIconSvg sx={{ color: "#4D44D8" }} />
                      </Box>
                      <Typography
                        variant="fw700s14c3D4B5A"
                        sx={{ color: "#2E2E2E", lineHeight: "20px" }}
                      >
                        Filters
                      </Typography>
                    </Box>
                    <Typography
                      variant="fw500s12lh246D72F6"
                      onClick={(e) => { e.stopPropagation(); handleResetAll(); }}
                      sx={{ cursor: "pointer", "&:hover": { color: "#4D44D8" } }}
                    >
                      Reset
                    </Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ paddingTop: 0 }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>

                    {/* The group carries all three dimensions, so any one of the
                        three keys turns it on. Gating on location alone would have
                        dropped customer filtering for an org that had configured
                        fleetName/fleetCategory and not location, with nothing in
                        its place and no way to configure one without the other. */}
                    {(filtersConfig.location ||
                      filtersConfig.fleetName ||
                      filtersConfig.fleetCategory) && (
                      canUseGlobalAdvancedFilter && globalFilterConfig ? (
                        <GlobalFilter
                          key={`customerLocation-${resetKey}`}
                          allFleets={allFleets || []}
                          allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
                          selectedFleetIds={selectedFleetIds || []}
                          selectedLocations={filters.location || []}
                          direction="vertical"
                          onChange={handleGlobalFilterChange}
                        />
                      ) : (
                        <LocationFilter
                          key={`location-${resetKey}`}
                          locations={allOnboardingLocationsHierarchy}
                          selectedLocations={selectedLocation}
                          onLocationChange={handleLocationChange}
                        />
                      )
                    )}

                    {filtersConfig.sohCategory && (
                      <SOHCategoryFilter
                        key={`soh-${resetKey}`}
                        selectedValues={filters.sohCategories || []}
                        onChange={(vals) =>
                          onFiltersChange && onFiltersChange({ sohCategories: vals })
                        }
                      />
                    )}

                    {/* String-level filters: a battery matches when ANY of its strings does */}
                    {filtersConfig.stringSoh && (
                      <SOHCategoryFilter
                        key={`stringSoh-${resetKey}`}
                        includeUnavailable={false}
                        title={getFilterLabel(filtersConfig.stringSoh, "String SOH")}
                        selectedValues={filters.stringSohCategories || []}
                        onChange={(vals) =>
                          onFiltersChange && onFiltersChange({ stringSohCategories: vals })
                        }
                      />
                    )}

                    {isSohConfidenceFilterEnabled(filtersConfig.sohConfidence) && (
                      <StaticOptionsFilter
                        key={`sohConfidence-${resetKey}`}
                        title={getFilterLabel(filtersConfig.sohConfidence, "SOH Estimation Confidence")}
                        options={getSohConfidenceOptions(filtersConfig.sohConfidence)}
                        selectedValues={filters.sohConfidence || []}
                        onChange={(vals) =>
                          onFiltersChange && onFiltersChange({ sohConfidence: vals })
                        }
                      />
                    )}

                    {filtersConfig.stringSohConfidence && (
                      <StaticOptionsFilter
                        key={`stringSohConfidence-${resetKey}`}
                        title={getFilterLabel(filtersConfig.stringSohConfidence, "String SOH Confidence")}
                        options={STRING_SOH_CONFIDENCE_OPTIONS}
                        selectedValues={filters.stringSohConfidence || []}
                        onChange={(vals) =>
                          onFiltersChange && onFiltersChange({ stringSohConfidence: vals })
                        }
                      />
                    )}

                    {filtersConfig.configuration && (
                      <StaticOptionsFilter
                        key={`configuration-${resetKey}`}
                        title={getFilterLabel(filtersConfig.configuration, "Configuration")}
                        options={configOptions}
                        selectedValues={filters.configuration || []}
                        onChange={(vals) =>
                          onFiltersChange && onFiltersChange({ configuration: vals })
                        }
                      />
                    )}

                    {filtersConfig.cellChemistry && cellChemistryOptions.length > 0 && (
                      <StaticOptionsFilter
                        key={`cellChemistry-${resetKey}`}
                        title={getFilterLabel(filtersConfig.cellChemistry, "Cell Chemistry")}
                        options={cellChemistryOptions}
                        selectedValues={filters.cellChemistry || []}
                        onChange={(vals) =>
                          onFiltersChange && onFiltersChange({ cellChemistry: vals })
                        }
                      />
                    )}

                    {filtersConfig.protocol && protocolOptions.length > 0 && (
                      <StaticOptionsFilter
                        key={`protocol-${resetKey}`}
                        title={getFilterLabel(filtersConfig.protocol, "Protocol")}
                        options={protocolOptions}
                        selectedValues={filters.protocol || []}
                        onChange={(vals) =>
                          onFiltersChange && onFiltersChange({ protocol: vals })
                        }
                      />
                    )}

                    {filtersConfig.manufacturingYear && mfgYearOptions.length > 0 && (
                      <StaticOptionsFilter
                        key={`manufacturingYear-${resetKey}`}
                        title="Manufacturing Year"
                        options={mfgYearOptions}
                        selectedValues={filters.manufacturingYear || []}
                        onChange={(vals) =>
                          onFiltersChange && onFiltersChange({ manufacturingYear: vals })
                        }
                      />
                    )}

                    {/* Odometer range: filters the SOH-anchored odometer behind the
                        "Odometer (km)" column, not the vehicle's current reading. */}
                    {filtersConfig.odometer && (
                      <NumericRangeFilter
                        key={`odometer-${resetKey}`}
                        title={getFilterLabel(filtersConfig.odometer, "Odometer (km)")}
                        unit={getFilterUnit(filtersConfig.odometer, "km")}
                        selectedRange={filters.odometer}
                        onChange={(range) =>
                          onFiltersChange && onFiltersChange({ odometer: range })
                        }
                        testIdPrefix="odometer"
                      />
                    )}

                    {filtersConfig.operationalStatus && (
                      <OperationalStatusFilter
                        key={`operationalStatus-${resetKey}`}
                        selectedOperationalStatus={filters.operationalStatus || []}
                        onOperationalStatusChange={(vals) =>
                          onFiltersChange && onFiltersChange({ operationalStatus: vals })
                        }
                      />
                    )}

                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default SOHDetailsSettingsSidebar;
